import { useState } from "react";
import { FiArchive, FiCalendar, FiCloud, FiEye, FiTrash2 } from "react-icons/fi";
import {
	DropdownItem,
	DropdownMenu,
	DropdownMenuDrawer,
	DropdownMenuTrigger,
} from "components/RFQ/MenuDropdown";
import Modal from "newui/components/ui/Modal/Modal";
import { showToast } from "utils/helpers";
import { isRfxStatusMatch } from "newui/features/Rfx/utils";
import { RFX_STATUS } from "utils/constants";
import { useSaveRFXTemplateFromExisting } from "actions/RFQ/templateAction";
import { useRfxNavigate } from "app/useRfxNavigate";
import type { Rfx } from "../../types/rfx.types";
import {
	useArchiveRfx,
	useForceDeleteRfx,
	useUnarchiveRfx,
} from "./rfx-row-actions-api";

export interface RfxRowActionsProps {
	rfx: Rfx;
	/**
	 * Row's click-through target, already resolved by the caller (it owns the
	 * `linkOverride` / draft-vs-view routing). Powers the menu's View/Edit item.
	 */
	viewHref?: string;
	onExtendClick: (rfx: Rfx) => void;
}

/**
 * Row-level Archive/Unarchive, Extend Deadline, Save-as-Template actions —
 * rebuild of the legacy `EditDropdown` (`components/Pages/RFQ/EditDropdown`)
 * for `TendersQuotesList` (PRO-637 Task 018).
 *
 * The ONE rule this rebuild exists to enforce: every action's availability +
 * the Archive-vs-Restore label is decided from `rfx.status` alone, NEVER
 * from the active tab. The legacy dropdown decided Archive/Restore off
 * `currentTab === "archived"`. Drafts and Archived are separate tabs again
 * (016), but keying off the tab is still wrong: the same list component
 * renders every scope, and a row's status is what decides whether Archive or
 * Restore applies. `rfx.status` is the only field that actually tells you
 * which one a given row is.
 *
 * Availability matrix, ported from legacy (minus the tab-key branch):
 * - Archive/Unarchive: always offered, on every status — label is
 *   "Restore" when `status === ARCHIVED`, "Archive" otherwise.
 * - Save as Template: legacy withheld this only for DRAFT rows (which show
 *   the Edit action instead — 008 already renders that as a row link, not
 *   here); offered on every other status, including ARCHIVED.
 * - Extend Deadline: legacy `isRFQSubmissionDateExtendable` — ACTIVE only.
 *
 * PRESENTATION: rendered as the legacy 3-dot kebab (`DropdownMenu` +
 * `DropdownMenuTrigger icon`), matching `EditDropdown`'s affordance. Task 018
 * originally shipped these as inline icon buttons spread across the card's top
 * edge, which was flagged as an unresolved escalation in `RFQCard.js`'s header
 * and is corrected here — the status-driven availability logic above is
 * unchanged, only how it's presented.
 */
export const RfxRowActions = ({
	rfx,
	onExtendClick,
	viewHref,
}: RfxRowActionsProps) => {
	const navigate = useRfxNavigate();
	const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const forceDeleteMutation = useForceDeleteRfx();
	const archiveMutation = useArchiveRfx();
	const unarchiveMutation = useUnarchiveRfx();
	const { mutateAsync: saveTemplate, isPending: isSavingTemplate } =
		useSaveRFXTemplateFromExisting();

	const isDraft = isRfxStatusMatch(rfx.status, RFX_STATUS.DRAFT);
	const isArchived = isRfxStatusMatch(rfx.status, RFX_STATUS.ARCHIVED);
	const isExtendable = isRfxStatusMatch(rfx.status, RFX_STATUS.ACTIVE);
	const archiveActionLabel = isArchived ? "Restore" : "Archive";
	const isArchiveActionPending = isArchived
		? unarchiveMutation.isPending
		: archiveMutation.isPending;

	const handleArchiveConfirm = async () => {
		try {
			if (isArchived) {
				await unarchiveMutation.mutateAsync(rfx.id);
				showToast("RFx retrieved from archive.", "Success", true);
			} else {
				await archiveMutation.mutateAsync(rfx.id);
				showToast("RFx archived.", "Success", true);
			}
			setShowArchiveConfirm(false);
		} catch {
			showToast(
				`Failed to ${archiveActionLabel.toLowerCase()} RFx. Please try again.`,
				"Error"
			);
		}
	};

	const handleDeleteConfirm = async () => {
		try {
			await forceDeleteMutation.mutateAsync(rfx.id);
			showToast("Draft RFx permanently deleted.", "Success", true);
			setShowDeleteConfirm(false);
		} catch {
			showToast("Failed to delete draft RFx. Please try again.", "Error");
		}
	};

	const handleSaveTemplate = async () => {
		try {
			await saveTemplate({ name: rfx.name, rfq_id: rfx.id });
			showToast("Saved as template.", "Success", true);
		} catch {
			showToast("Failed to save as template. Please try again.", "Error");
		}
	};

	return (
		<div data-testid={`rfx-row-actions-${rfx.id}`}>
			<DropdownMenu>
				<DropdownMenuTrigger
					icon
					noBg
					mutedIcon
					data-testid={`rfx-row-actions-trigger-${rfx.id}`}
				/>
				<DropdownMenuDrawer>
					{viewHref && (
						<DropdownItem
							className="w-full"
							onClick={() => navigate(viewHref)}
							data-testid={`rfx-row-view-${rfx.id}`}
						>
							<FiEye size={16} aria-hidden="true" />
							<p className="font-roboto text-sm font-medium text-secondary-700">
								{isDraft ? "Edit RFx" : "View RFx"}
							</p>
						</DropdownItem>
					)}

					{!isDraft && (
						<DropdownItem
							// `DropdownItem` renders a div, not a button — `disabled`
							// is inert on it, so the in-flight guard has to be in the
							// handler (and reflected visually) rather than declarative.
							className={`w-full ${isSavingTemplate ? "pointer-events-none opacity-50" : ""}`}
							onClick={() => {
								if (isSavingTemplate) {
									return;
								}
								handleSaveTemplate();
							}}
							data-testid={`rfx-row-save-template-${rfx.id}`}
						>
							<FiCloud size={16} aria-hidden="true" />
							<p className="font-roboto text-sm font-medium text-secondary-700">
								{isSavingTemplate ? "Saving…" : "Save as Template"}
							</p>
						</DropdownItem>
					)}

					{isExtendable && (
						<DropdownItem
							className="w-full"
							onClick={() => onExtendClick(rfx)}
							data-testid={`rfx-row-extend-deadline-${rfx.id}`}
						>
							<FiCalendar size={16} aria-hidden="true" />
							<p className="font-roboto text-sm font-medium text-secondary-700">
								Extend Deadline
							</p>
						</DropdownItem>
					)}

					{/*
					 * Drafts are permanently deleted, never archived — archiving
					 * an unpublished draft leaves a row nobody can reach. Every
					 * other status keeps the reversible archive action.
					 */}
					{isDraft ? (
						<DropdownItem
							className="w-full"
							onClick={() => setShowDeleteConfirm(true)}
							data-testid={`rfx-row-delete-${rfx.id}`}
						>
							<FiTrash2
								size={16}
								aria-hidden="true"
								className="text-error-600"
							/>
							<p className="font-roboto text-sm font-medium text-error-600">
								Delete
							</p>
						</DropdownItem>
					) : (
						<DropdownItem
							className="w-full"
							onClick={() => setShowArchiveConfirm(true)}
							data-testid={`rfx-row-archive-toggle-${rfx.id}`}
						>
							<FiArchive size={16} aria-hidden="true" />
							<p className="font-roboto text-sm font-medium text-secondary-700">
								{archiveActionLabel}
							</p>
						</DropdownItem>
					)}
				</DropdownMenuDrawer>
			</DropdownMenu>

			{showDeleteConfirm && (
				<Modal
					isOpen={showDeleteConfirm}
					onClose={() => setShowDeleteConfirm(false)}
					title={`Delete ${rfx.name}?`}
					description={`This will permanently delete ${rfx.name} and all of its content. This action cannot be undone.`}
					iconType="warning"
					primaryButton={{
						text: forceDeleteMutation.isPending
							? "Deleting…"
							: "Yes, delete permanently",
						isLoading: forceDeleteMutation.isPending,
						onClick: handleDeleteConfirm,
					}}
					secondaryButton={{
						text: "Cancel",
						disabled: forceDeleteMutation.isPending,
						onClick: () => setShowDeleteConfirm(false),
					}}
				>
					<></>
				</Modal>
			)}

			{showArchiveConfirm && (
				<Modal
					isOpen={showArchiveConfirm}
					onClose={() => setShowArchiveConfirm(false)}
					title={`${archiveActionLabel} ${rfx.name}?`}
					description={`Are you sure you want to ${archiveActionLabel.toLowerCase()} ${rfx.name}?`}
					iconType={isArchived ? "success" : "warning"}
					primaryButton={{
						text: isArchiveActionPending
							? `${archiveActionLabel}ing…`
							: "Yes",
						isLoading: isArchiveActionPending,
						onClick: handleArchiveConfirm,
					}}
					secondaryButton={{
						text: "Cancel",
						disabled: isArchiveActionPending,
						onClick: () => setShowArchiveConfirm(false),
					}}
				>
					<></>
				</Modal>
			)}
		</div>
	);
};
